// Shared display + formatting helpers used by both DV360 and TTD branches
// of the Campaign Launch Workflow. These are intentionally platform-neutral —
// per-platform encoders (micros → dollars, IO displayName parsers, etc.) live
// next to the platform that owns them (dv360/, etc.). No React coupling.

// DV360 launcher serialises dates as { year, month, day }. TTD serialises them
// as ISO strings. Normalise to YYYY-MM-DD or return null.
export const ymdFromObj = (d) => {
  if (!d) return null;
  if (typeof d === 'string') return d.slice(0, 10);
  if (d.year && d.month && d.day) return `${d.year}-${String(d.month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`;
  return null;
};

export const fmtDate = (s) => {
  if (!s) return '—';
  try {
    const d = new Date(s + 'T00:00:00Z');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  } catch { return s; }
};

export const fmtCurrency = (n) => (n == null
  ? '—'
  : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`);

/**
 * Money in the ACCOUNT'S currency. fmtCurrency hardcodes '$', which is wrong
 * the moment a plan is imported from a non-USD account (an INR campaign's
 * ₹1,000/day pacing rendered as "$1,000/day"). Falls back to fmtCurrency when
 * the plan carries no currency code, so USD-era callers are unchanged.
 */
export const fmtMoney = (n, code) => {
  if (n == null) return '—';
  if (!code) return fmtCurrency(n);
  try {
    return Number(n).toLocaleString(undefined, {
      style: 'currency', currency: code, minimumFractionDigits: 0, maximumFractionDigits: 2,
    });
  } catch {
    // Unknown/garbled code: show it rather than silently claiming dollars.
    return `${code} ${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  }
};

export const fmtImps = (n) => (n == null ? '—' : Number(n).toLocaleString());

export function fmtBadgeClass(fmt) {
  const k = String(fmt || '').toLowerCase();
  if (k.includes('mob') || k.includes('display')) return 'clw-fmt-mob';
  if (k.includes('video') || k.includes('olv')) return 'clw-fmt-olv';
  if (k.includes('ott') || k.includes('ctv') || k.includes('connect')) return 'clw-fmt-ott';
  if (k.includes('crm')) return 'clw-fmt-crm';
  if (k.includes('conq')) return 'clw-fmt-conq';
  if (k.includes('lunch') || k === 'l&l') return 'clw-fmt-ll';
  return 'clw-fmt-other';
}

// Derive a format pill code from a free-text name / tactic. Shared by DV360
// (line item displayName) and TTD (ad group name) — both adopt the same
// pill vocabulary (OLV, OTT, CRM, CONQ, L&L, MOB, DISP).
export function deriveFormat(name = '', tactic = '') {
  const s = (name + ' ' + tactic).toLowerCase();
  if (s.includes('olv') || s.includes('video') || s.includes('vid')) return 'OLV';
  if (s.includes('ott') || s.includes('ctv') || s.includes('connected')) return 'OTT';
  if (s.includes('crm')) return 'CRM';
  if (s.includes('conquesting') || s.includes('conq')) return 'CONQ';
  if (s.includes('lunch & learn') || s.includes('lunch and learn')) return 'L&L';
  if (s.includes('mob') || s.includes('mobile')) return 'MOB';
  return 'DISP';
}

// Format a cell value for the review grid based on its column's `fmt` hint.
// `currencyCode` is the row's account currency (gads rows carry it) — without
// it an INR campaign's budget column rendered "$836,400".
export function fmtCellValue(val, colFmt, currencyCode = null, row = null) {
  if (val == null || val === '') return '—';
  if (typeof colFmt === 'function') return colFmt(val, row);
  if (colFmt === 'cur') return typeof val === 'number' ? fmtMoney(val, currencyCode) : String(val);
  if (colFmt === 'num') return typeof val === 'number' ? fmtImps(val) : String(val);
  if (colFmt === 'date') return fmtDate(val);
  return String(val);
}
